// ** React Imports
import { useState, Fragment, useEffect } from 'react'
// ** Next Import
import { useNavigate } from 'react-router-dom'
// ** MUI Imports
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import Badge from '@mui/material/Badge'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
// ** Icons Imports
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import Vector from 'src/assets/Vector.svg'
// ** Context
import { useAuth } from 'src/hooks/useAuth'
import MyProfileIcon from 'src/@core/components/icons/MyProfileIcon'
import BackToOujatIcon from 'src/@core/components/icons/BackToOujatIcon'
import HelpIcon from 'src/@core/components/icons/HelpIcon'
import { ApiEndPoints, MEDIA_URL } from 'src/network/endpoints'
import User from '../../../../assets/account.png'
import { useTranslation } from 'react-i18next'
import { axiosInstance } from 'src/network/adapter'
import { toastError } from 'src/utils/utils'
// ** Styled Components
const BadgeContentSpan = styled('span')(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
}))
const UserDropdown = props => {
  // ** States
  const [anchorEl, setAnchorEl] = useState(null)
  const [loadingHelp, setLoadingHelp] = useState(false)
  // ** Hooks
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const { t } = useTranslation()
  // Add useEffect to close dropdown when component unmounts
  useEffect(() => {
    return () => {
      setAnchorEl(null)
    }
  }, [])
  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget)
  }
  const handleDropdownClose = () => {
    setAnchorEl(null)
  }
  // Handle navigation and close dropdown
  const handleNavigation = url => {
    setAnchorEl(null) // Close dropdown immediately
    if (url) {
      // Use setTimeout to ensure dropdown closes before navigation
      setTimeout(() => {
        navigate(url)
      }, 0)
    }
  }
  // Add a helper to check if user can access services
  const canAccessService = user?.existService === true
  // Updated menu item styles based on the screenshot
  const menuItemStyles = {
    py: 2,
    px: 4,
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      fontSize: '1.25rem',
      mr: 3,
      color: 'text.secondary'
    }
  }
  const activeMenuItemStyles = {
    ...menuItemStyles,
    '& .MuiTypography-root': {
      fontWeight: 600,
      color: 'text.primary'
    }
  }
  const inactiveMenuItemStyles = {
    ...menuItemStyles,
    borderLeft: '3px solid transparent',
    '& .MuiTypography-root': {
      color: 'text.secondary'
    }
  }
  const handleLogout = () => {
    setAnchorEl(null) // Close dropdown immediately
    logout()
  }

  const handleBackToOujat = () => {
    setAnchorEl(null) // Close dropdown immediately
    window.open('https://www.oujat.com', '_blank', 'noopener,noreferrer')
  }

  // Handle Help navigation - create/get conversation first
  const handleHelpClick = async () => {
    if (!canAccessService) return

    setAnchorEl(null) // Close dropdown immediately
    setLoadingHelp(true)

    try {
      // Call API to create/get agentAdminChat conversation
      const response = await axiosInstance.post(ApiEndPoints.Chat.adminUserConversation('agentAdminChat'))

      if (response.data?.status === 'success' && response.data?.data?.conversation?._id) {
        const conversationId = response.data.data.conversation._id
        const conversation = response.data.data.conversation

        // Navigate to help page with conversation ID and conversation data
        navigate('/help', {
          state: { conversationId, conversation }
        })
      } else {
        toastError(t('common.failedCreateConversation'))
      }
    } catch (error) {
      console.error('Error creating conversation:', error)
      toastError(error?.response?.data?.message || 'Failed to create conversation')
    } finally {
      setLoadingHelp(false)
    }
  }

  const capitalizeFirstLetter = str => {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }
  return (
    <Fragment>
      <Box
        onClick={handleDropdownOpen}
        ml={2}
        display='flex'
        sx={{
          bgcolor: 'common.white',
          border: '1px solid #E7E9ED',
          borderRadius: '8px',
          padding: 2,
          cursor: 'pointer'
        }}
        alignItems='center'
        gap={2}
      >
        <Avatar
          alt={user?.firstName}
          sx={{ width: 32, height: 32 }}
          src={user?.profileImg ? `${MEDIA_URL}${user.profileImg}` : User}
        />
        <Typography sx={{ fontWeight: 600, fontSize: { xs: "13px", sm: '16px' } }}>{`${user?.firstName} ${user?.lastName}`}</Typography>
        <img src={Vector} style={{ marginRight: 3 }} alt='Vector' />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={{
          '& .MuiMenu-paper': {
            width: 250,
            mt: 1,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px'
          }
        }}
      >
        <Box sx={{ pt: 2, pb: 3, px: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              border: '1px solid #E7E9ED',
              p: 2,
              mb: 2
            }}
          >
            <Badge
              overlap='circular'
              badgeContent={<BadgeContentSpan />}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
            >
              <Avatar
                alt={`${user?.firstName} ${user?.lastName}`}
                src={user?.profileImg ? `${MEDIA_URL}${user?.profileImg}` : User}
                sx={{ width: '40px', height: '40px' }}
              />
            </Badge>
            <Box sx={{ display: 'flex', ml: 3, alignItems: 'flex-start', flexDirection: 'column' }}>
              <Typography variant='fm-p2' sx={{ fontWeight: 500, color: '#000000' }}>
                {`${capitalizeFirstLetter(user?.firstName)} ${capitalizeFirstLetter(user?.lastName)}`}
              </Typography>
              <Typography
                variant='body2'
                sx={{ fontSize: '13px', color: '#8F9DAC !important', fontWeight: 700, textTransform: 'capitalize' }}
              >
                {user?.role}
              </Typography>
            </Box>
          </Box>
          {/* Profile Menu Item - Active state based on the screenshot */}
          <MenuItem sx={activeMenuItemStyles} onClick={() => handleNavigation('/profile')}>
            <MyProfileIcon />
            <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
              {t('Profile Section.My Profile')}
            </Typography>
          </MenuItem>
          {/* Back to Oujat.com Menu Item - With specified orange color */}
          <MenuItem sx={inactiveMenuItemStyles} onClick={handleBackToOujat}>
            <Box>
              <BackToOujatIcon />
            </Box>
            <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
              {t('Profile Section.Back to')}{' '}
              <Box component='span' sx={{ color: '#FF5722' }}>
                {t('Profile Section.Oujat.com')}
              </Box>
            </Typography>
          </MenuItem>
          {/* Help Menu Item */}
          <MenuItem sx={inactiveMenuItemStyles} onClick={handleHelpClick} disabled={!canAccessService || loadingHelp}>
            <HelpIcon />
            <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
              {loadingHelp ? 'Loading...' : t('Profile Section.Help')}
            </Typography>
          </MenuItem>
        </Box>
        <Divider sx={{ my: 1 }} />
        {/* Logout option */}
        <MenuItem sx={inactiveMenuItemStyles} onClick={handleLogout}>
          <LogoutOutlinedIcon sx={{ fontSize: 24, color: '#8F9DAC !important' }} />
          <Typography variant='fm-p1' sx={{ fontWeight: 700, color: '#8F9DAC !important' }}>
            {t('Profile Section.Logout')}
          </Typography>
        </MenuItem>
      </Menu>
    </Fragment>
  )
}
export default UserDropdown
